import {
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { Alert, ExternalLink, Text, api, getQueryProps, isDefined, isTruthy } from 'common';

import { EMPTY_CONTENT } from '../../../../../../utils/constants';

interface OutputDocumentUrlSectionProps {
  fileReferenceId?: string;
  title: string;
}

export function OutputDocumentUrlSection(props: OutputDocumentUrlSectionProps) {
  const { fileReferenceId, title } = props;
  const { data, isPending, isError, error } = useQuery({
    ...getQueryProps(api.client.File, 'generateReadUrl', fileReferenceId ?? 'INVALID_ID'),
    enabled: isTruthy(fileReferenceId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (!isTruthy(fileReferenceId)) {
    return null;
  }

  if (isPending) {
    return <Skeleton variant="rectangular" width="100%" height={48} />;
  }

  if (isError) {
    return (
      <Stack gap={1}>
        <Alert error={error} title="Failed to get document url" />
        <Text>File Reference: {fileReferenceId}</Text>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Text variant="heading4" bold>
        {title}
      </Text>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>URL:</TableCell>
              <TableCell sx={{ wordBreak: 'break-all' }}>
                {isDefined(data?.signedReadUrl) ? (
                  <ExternalLink href={data.signedReadUrl}>{fileReferenceId}</ExternalLink>
                ) : (
                  <>{EMPTY_CONTENT}</>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
